import { SubjectKind } from '@/subjects';
import type { ExtractAtomValue } from 'jotai';
import type { LinkProps } from '@tanstack/react-router';
import { formatDate } from '@/dates';
import { failsModeFilter } from '@/filters';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { GetExtractionsTarget } from '@/features/extractions/constants';
import { standardDeviationGroupKey } from '@/features/standard-deviations/utils';
import { tasksDisplayAtom, tasksFilterAtom } from '@/features/tasks/store';
import { TASK_STATUS_VALUES, type MY_TASKS_TABS } from '@/features/tasks/constants';
import type { Task, TaskAssignee, TaskFinding, TaskListFilters } from '@/features/tasks/types';

export const taskTitleFromComment = (content: string, names: string[]) => {
  const title = names
    .reduce((text, name) => text.replaceAll(`@${name}`, ''), content)
    .replace(/\s+/g, ' ')
    .trim();
  return title || content;
};
const taskFindingScope = (task: Task, finding: TaskFinding) => ({
  projectId: finding.sourceLocation?.projectId ?? task.projectId,
  workspaceId: finding.sourceLocation?.workspaceId ?? task.workspaceId,
  resourceId: finding.sourceLocation?.resourceId ?? task.resourceId,
  resourceType: finding.sourceLocation?.resourceType ?? task.resourceType,
});

export const taskListStatuses = (filter: ExtractAtomValue<typeof tasksFilterAtom>) =>
  TASK_STATUS_VALUES.filter((status) => !failsModeFilter(filter.status, status, filter.statusMode));

export const taskListFilters = ({
  user,
  projectId,
  workspaceId,
  filter,
  display,
  search,
}: {
  user: { id: string; organizationId: string };
  projectId: string;
  workspaceId: string | undefined;
  filter: ExtractAtomValue<typeof tasksFilterAtom>;
  display: ExtractAtomValue<typeof tasksDisplayAtom>;
  search: string;
}): TaskListFilters | null => {
  const statuses = taskListStatuses(filter);
  if (!statuses.length) return null;
  return {
    organizationId: user.organizationId,
    projectId,
    workspaceId,
    assignedTo: (display.tab ?? 'assigned') === 'assigned' ? user.id : undefined,
    createdBy: display.tab === 'created' ? user.id : undefined,
    includeArchived: display.showArchived ?? false,
    status: statuses.join(','),
    search,
    orderBy: display.order.field === 'dueDate' ? 'due_date' : 'updated_at',
    orderDirection: display.order.direction,
  };
};

export const myTaskListStatuses = (
  filter: ExtractAtomValue<typeof tasksFilterAtom>,
  tab: (typeof MY_TASKS_TABS)[number],
) => taskListStatuses(filter).filter((status) => tab !== 'today' || status !== 'closed');

export const myTaskListFilters = ({
  user,
  tab,
  filter,
  display,
  search,
}: {
  user: { id: string; organizationId: string };
  tab: (typeof MY_TASKS_TABS)[number];
  filter: ExtractAtomValue<typeof tasksFilterAtom>;
  display: ExtractAtomValue<typeof tasksDisplayAtom>;
  search: string;
}): TaskListFilters | null => {
  const statuses = myTaskListStatuses(filter, tab);
  if (!statuses.length) return null;
  const today = tab === 'today';
  return {
    organizationId: user.organizationId,
    assignedTo: tab === 'created' ? undefined : user.id,
    createdBy: tab === 'created' ? user.id : undefined,
    includeArchived: !today && (display.showArchived ?? false),
    status: statuses.join(','),
    search,
    orderBy: today || display.order.field === 'dueDate' ? 'due_date' : 'updated_at',
    orderDirection: today ? 'asc' : display.order.direction,
  };
};

export const dueDateState = (dueDate: string) => {
  const now = new Date();
  const today = [now.getFullYear(), now.getMonth() + 1, now.getDate()]
    .map((part) => String(part).padStart(2, '0'))
    .join('-');
  const day = dueDate.slice(0, 10);
  if (day < today) return 'past';
  if (day === today) return 'today';
  return 'future';
};

export const isDueByToday = (task: Pick<Task, 'dueDate'>) =>
  !!task.dueDate && dueDateState(task.dueDate) !== 'future';

export const taskAssigneeName = (assignee: TaskAssignee) =>
  [assignee.firstName, assignee.lastName].filter(Boolean).join(' ') || assignee.email || '';

export const taskFindingLink = (task: Task, finding: TaskFinding): LinkProps | null => {
  const { projectId, workspaceId, resourceId, resourceType } = taskFindingScope(task, finding);
  const location = finding.sourceLocation ?? {};
  const entityId = finding.entityId;
  const kind = finding.subjectKind;
  if (!projectId || !resourceId || !entityId) return null;
  const search = { taskId: undefined };
  if (kind === SubjectKind.ARTIFACT_SHEET_ROW && location.artifactId)
    return {
      to: '/project/$projectId/workspaces/$workspaceId/scope-breakdown',
      params: { projectId, workspaceId: workspaceId || resourceId },
      search: {
        ...search,
        sheetId: location.artifactId,
        sheetTab: location.tabId,
        sheetRow: entityId,
      },
    };
  if (resourceType === DOCUMENT_TYPE.bid) {
    if (kind !== 'custom_topic_statement') return null;
    return {
      to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/summary',
      params: { projectId, workspaceId, bidId: resourceId },
      search: { ...search, targetStatementId: entityId },
    };
  }
  const params = { projectId, workspaceId: resourceId };
  if (kind === 'ambiguity_statement')
    return {
      to: '/project/$projectId/workspaces/$workspaceId/ambiguities/$ambiguityId',
      params: { ...params, ambiguityId: entityId },
      search,
    };
  if (kind === 'evaluation_conflict_finding')
    return {
      to: '/project/$projectId/workspaces/$workspaceId/conflicts/$conflictId',
      params: { ...params, conflictId: entityId },
      search,
    };
  if (
    (kind === 'legal_deviation_cluster' || kind === 'legal_deviation_statement') &&
    location.domain &&
    location.clusterId
  )
    return {
      to: '/project/$projectId/workspaces/$workspaceId/contract-review/$domain',
      params: { ...params, domain: location.domain },
      search: { ...search, clusterId: location.clusterId },
    };
  if (kind === 'clause_scanner_clause' && location.subcategoryId)
    return {
      to: '/project/$projectId/workspaces/$workspaceId/clauses/$subcategoryId/$clauseId',
      params: { ...params, subcategoryId: location.subcategoryId, clauseId: entityId },
      search,
    };
  if (kind === 'standard_deviation_statement' && location.standardDocId)
    return {
      to: '/project/$projectId/workspaces/$workspaceId/standard-deviations/$deviationId',
      params: {
        ...params,
        deviationId: standardDeviationGroupKey({
          id: entityId,
          standardDocId: location.standardDocId,
          standardRecordId: location.standardRecordId,
          standardChunkIndex: location.standardChunkIndex ?? null,
        }),
      },
      search: { ...search, statementId: entityId },
    };
  if (kind !== 'custom_topic_statement' && kind !== 'custom_topic_query') return null;
  const statementId = kind === 'custom_topic_statement' ? entityId : undefined;
  if (location.extractionTarget === GetExtractionsTarget.OVERVIEW)
    return {
      to: '/project/$projectId/workspaces/$workspaceId/summary',
      params,
      search: { ...search, targetStatementId: entityId },
    };
  const queryId = kind === 'custom_topic_query' ? entityId : location.queryId;
  if (!location.extractionDefinitionId || !queryId) return null;
  return {
    to: '/project/$projectId/workspaces/$workspaceId/extractions',
    params,
    search: {
      ...search,
      extractionId: location.extractionDefinitionId,
      queryId,
      id: statementId,
    },
  };
};

export const formatDueDate = (
  dueDate: string,
  locale: string,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' },
) => formatDate(new Date(dueDate.slice(0, 10)), { ...options, timeZone: 'UTC' }, locale);
